import { useCallback, useMemo } from 'react';
import { usePostHog } from 'posthog-js/react';
import classNames from 'classnames';

import { Flag } from '../../../global/Rewards';
import {
  captureAttributesToProperties,
  getActionType,
  getEventName,
  getTrackerId,
  POSTHOG_CAPTURE_ATTRIBUTES,
  POSTHOG_PROPERTIES,
  TRACKER_IDS,
} from '../../../config/tracker';
import { REWARD_AMOUNT_TYPES, REWARD_TYPE } from '../../../config/rewards';
import {
  isLimitErrorCode,
  isLockedErrorCode,
  isNotFoundErrorCode,
  isUserLimitErrorCode,
  isGiveAwayType,
} from '../../../utils/rewards';
import { useFirebaseAuth } from '../../../../global/FirebaseProvider/FirebaseProvider';
import ChevronUpSvg from '../../../global/SvgComponents/ChevronUp_svg';
import IconGemCircle from '../../../images/icon-gem-circle.svg';
import IconAwardCircle from '../../../images/app_icon_award.webp';
import Lock from '../../../images/icon-lock-circle.svg';
import { TAKE_BACK_BAG_COLLECTION_URL } from '../../../config/externalURL';

import './RewardItem.scss';
import { insertTCIcon, TBB_TC_ICON } from '../../../utils/tbbRegistration';
import { formatReadableNumber } from '../../../utils/numbers';

const {
  rewards: {
    list: {
      rewardItem: trackerRewardItem,
      claimButton: trackerClaimButton,
    },
  },
} = TRACKER_IDS;

function getDetailsLabel(type) {
  switch (type) {
    case REWARD_TYPE.GIVEAWAY:
      return 'Giveaway details';
    case REWARD_TYPE.DONATION:
      return 'Donation details';
    default:
      return 'Reward details';
  }
}

function getCtaLabelByType(type, exchangeValue) {
  switch (type) {
    case REWARD_TYPE.GIVEAWAY:
      return `Join now for ${TBB_TC_ICON} ${exchangeValue}`;
    case REWARD_TYPE.DONATION:
      return `Donate ${TBB_TC_ICON} ${exchangeValue}`;
    default:
      return `Get it for ${TBB_TC_ICON} ${exchangeValue}`;
  }
}

const getCtaLabel = ({
  isDeal,
  exchangeValue,
  type,
  disabled,
  status,
  hasSufficientFunds }) => {
  if (isDeal) {
    if (disabled) {
      return isLockedErrorCode(status) ? 'Recycle To Unlock' : 'Deal details';
    }
    return 'Claim Deal';
  }

  if (disabled) {
    if (!hasSufficientFunds) {
      return `Earn ${TBB_TC_ICON} ${formatReadableNumber(exchangeValue)} to unlock`;
    }
    if (isLockedErrorCode(status)) {
      return 'Recycle To Unlock';
    }

    return getDetailsLabel(type);
  }

  return getCtaLabelByType(type, formatReadableNumber(exchangeValue));
};

const RewardItem = ({
  rewardItem,
  onClick,
  rewardProperties,
  withCta = true,
  trackerProps = {},
  index,
}) => {
  const { userData, isSignedIn, user } = useFirebaseAuth();
  const posthog = usePostHog();

  const {
    balance: {
      currentBalance: currentBalanceData = 0,
    },
  } = userData;

  const {
    uid: rewardId,
    name: rewardName,
    description: rewardDescription,
    amountType,
    rewardAmount,
    type,
    exchangeValue,
    status,
    isNew,
    campaignEndDate,
    rewardCodes,
    allowTcCredits,
    store: {
      logoUrl,
      imgUrl,
      RewardLogoProperties,
      tcCreditAmount,
      tcCreditAmountType,
    },
  } = useMemo(() => rewardItem, [rewardItem]);

  const isGiveAway = isGiveAwayType(type);

  const isDeal = useMemo(() => Number(exchangeValue) === 0, [exchangeValue]);
  const hasSufficientFunds = useMemo(
    () => parseFloat(currentBalanceData) >= parseFloat(exchangeValue),
    [currentBalanceData, exchangeValue],
  );
  const lockedItem = useMemo(
    () => isLockedErrorCode(status) || !hasSufficientFunds,
    [status, hasSufficientFunds],
  );

  const disabled = useMemo(() => (
    isLimitErrorCode(status) || isUserLimitErrorCode(status) ||
    isNotFoundErrorCode(status) || lockedItem
  ), [status, lockedItem]);

  const isRecycleToUnlock = useMemo(
    () => isDeal && disabled && isLockedErrorCode(status),
    [status, disabled, isDeal],
  );

  const isRewardToUnlock = useMemo(
    () => disabled && !hasSufficientFunds && !isDeal,
    [hasSufficientFunds, disabled, isDeal],
  );
  const amountText = useMemo(() => (
    REWARD_AMOUNT_TYPES[amountType]?.getText({ rewardAmount, type }) ?? ''
  ), [amountType, rewardAmount, type, isGiveAway]);

  const handleOnClick = useCallback((fromCta = false) => {
    console.log('[RewardItem] handleOnClick called', { fromCta, isDeal, disabled, index, rewardId });

    const claim = fromCta && isDeal && !disabled;
    const tracker = claim ? trackerClaimButton : trackerRewardItem;

    posthog.capture(
      getEventName(tracker),
      {
        [POSTHOG_PROPERTIES.TRACKER_ID]: getTrackerId(tracker),
        [POSTHOG_PROPERTIES.ACTION_ID]: getActionType(tracker),
        [POSTHOG_PROPERTIES.REWARD_ID]: rewardId,
        [POSTHOG_PROPERTIES.REWARD_NAME]: rewardName,
        [POSTHOG_PROPERTIES.REWARD_IS_DEAL]: isDeal,
        ...(index != null && !Number.isNaN(Number(index)) && {
          [POSTHOG_PROPERTIES.LIST_POSITION]: Number(index) + 1,
        }),
        ...captureAttributesToProperties(trackerProps),
      },
    );

    if (lockedItem && fromCta) {
      console.log('[RewardItem] Locked item, redirecting to TBB');
      window.location.href = TAKE_BACK_BAG_COLLECTION_URL;
      return;
    }

    console.log('[RewardItem] Calling parent onClick with:', { rewardItem, fromCtaAndNotDisabled: fromCta && !disabled, index });
    onClick?.(
      rewardItem,
      fromCta && !disabled,
      index,
    );
  }, [
    rewardId, rewardName, onClick, isSignedIn, user,
    disabled, rewardItem, lockedItem, isDeal, index, trackerProps,
  ]);

  const statusIcon = useMemo(() => {
    if (lockedItem) {
      return (
        <div className="RewardsViewItem__lockedIcon">
          <img
            className="RewardsViewItem__lockedIcon--icon"
            src={disabled ? Lock : IconGemCircle}
            alt="reward item"
          />
        </div>
      );
    }

    if (isGiveAway) {
      return (
        <img
          className="RewardsViewItem__award"
          src={IconAwardCircle}
          alt="reward item"
        />
      );
    }

    if (!isDeal) {
      return (
        <img
          className="RewardsViewItem__gem"
          src={IconGemCircle}
          alt="reward item"
        />
      );
    }
    return null;
  }, [lockedItem, disabled, isDeal]);

  const rewardItemTrackerProps = useMemo(() => {
    const tracker = isDeal && !disabled ? trackerClaimButton : trackerRewardItem;

    return {
      [POSTHOG_CAPTURE_ATTRIBUTES.TRACKER_ID]: getTrackerId(tracker),
      [POSTHOG_CAPTURE_ATTRIBUTES.ACTION_ID]: getActionType(tracker),
      [POSTHOG_CAPTURE_ATTRIBUTES.REWARD_ID]: rewardId,
      [POSTHOG_CAPTURE_ATTRIBUTES.REWARD_NAME]: rewardName,
      [POSTHOG_CAPTURE_ATTRIBUTES.REWARD_IS_DEAL]: isDeal,
      ...(!Number.isNaN(index) && {
        [POSTHOG_CAPTURE_ATTRIBUTES.LIST_POSITION]: Number(index) + 1,
      }),
      ...trackerProps,
    };
  }, [isDeal, disabled, rewardId, rewardName, trackerProps, index]);

  const ctaLabel = useMemo(() => getCtaLabel({
    isDeal,
    exchangeValue,
    type,
    disabled,
    status,
    hasSufficientFunds,
  }), [isDeal, exchangeValue, type, disabled, status, hasSufficientFunds]);

  return (
    <div className={classNames('RewardsViewItem', { withCta, disabled })}>
      {statusIcon}
      <Flag
        status={status}
        isNew={isNew}
        isFreeReward={isDeal}
        hasRewardCodes={rewardCodes.length > 0}
        exchangeValue={exchangeValue}
        expireAt={campaignEndDate}
        allowTcCredits={allowTcCredits}
        tcCreditAmount={tcCreditAmount}
        tcCreditAmountType={tcCreditAmountType}
        properties={rewardProperties}
      />
      <button
        className={classNames('RewardsViewItem__caret', { disabled })}
        id={`RewardsViewItem__caret-${rewardItem.uid}`}
        onClick={() => handleOnClick()}
        type="button"
        aria-label="reward item caret"
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            handleOnClick();
          }
        }}
        {...rewardItemTrackerProps}
      >
        <div
          className="RewardsViewItem__caret--cover"
          alt="store image"
          style={{
            backgroundImage: `linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%), url(${imgUrl})`,
          }}
        >
          <img
            className="RewardsViewItem__caret--cover-logo"
            src={logoUrl}
            style={{
              ...RewardLogoProperties,
              ...(RewardLogoProperties?.height ? { maxHeight: RewardLogoProperties.height, height: 'unset' } : {}),
            }}
            alt="store logo"
          />
        </div>
        <div className="RewardsViewItem__caret--content">
          <div className="RewardsViewItem__caret--content-text">
            <p className="RewardsViewItem__caret--content-text-title" title={amountText}>{amountText}</p>
            <p className="RewardsViewItem__caret--content-text-subtitle" title={rewardDescription}>{rewardDescription}</p>
          </div>
          <div className="RewardsViewItem__caret--content-arrow">
            <ChevronUpSvg
              color="var(--trashie-ui-faded-black-80)"
              width={12}
            />
          </div>
        </div>
      </button>
      {withCta && (
        <button
          type="button"
          className="RewardsViewItem__cta"
          id={`RewardsViewItem__cta-${rewardItem.uid}`}
          onClick={() => handleOnClick(true)}
          aria-label="reward item action"
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              handleOnClick(true);
            }
          }}
          {...rewardItemTrackerProps}
          {...((isRecycleToUnlock || isRewardToUnlock) && { [POSTHOG_CAPTURE_ATTRIBUTES.TBB_SHOP_LINK]: 'true' })}
        >
          <span className={classNames('RewardsViewItem__cta--label', { RewardsViewItem__giveaway: isGiveAway })}>
            {insertTCIcon(ctaLabel)}
          </span>
        </button>
      )}
    </div>
  );
};

export default RewardItem;
